import { getQueryRetryDelay } from "Core/Query/queryRetryPolicy";
import { useQueryRetryHandler } from "Core/Query/useQueryRetryHandler";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { useQueryErrorHandler } from "./useQueryErrorHandler";

const ReactQueryDevtoolsProduction = React.lazy(async () => {
    const devtools = await import("@tanstack/react-query-devtools/build/modern/production.js");
    return {
        default: devtools.ReactQueryDevtools
    };
});

const ReactQueryProviderBase = (props: { children: React.ReactNode }) => {
    const [showDevtools, setShowDevtools] = React.useState(false);
    const retryHandler = useQueryRetryHandler();
    const { queryErrorHandler, mutationErrorHandler } = useQueryErrorHandler();

    const [queryClient] = useState(
        new QueryClient({
            defaultOptions: {
                queries: {
                    retry: retryHandler,
                    retryDelay: getQueryRetryDelay,
                    // Unused data is dropped 30 minutes after its last observer unmounts, so long sessions don't keep every response.
                    gcTime: 30 * 60 * 1000,
                    staleTime: 5 * 60 * 1000, // 5 minute
                    refetchOnWindowFocus: false,
                    retryOnMount: false
                }
            },
            queryCache: new QueryCache({
                onError: queryErrorHandler
            }),
            mutationCache: new MutationCache({
                onError: mutationErrorHandler
            })
        })
    );

    useEffect(() => {
        // @ts-ignore
        window.toggleDevtools = () => setShowDevtools((old) => !old);
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            {props.children}
            <ReactQueryDevtools initialIsOpen={false} position="right" buttonPosition="bottom-right" />
            {showDevtools && (
                <React.Suspense fallback={null}>
                    <ReactQueryDevtoolsProduction initialIsOpen={false} position="right" buttonPosition="bottom-right" />
                </React.Suspense>
            )}
        </QueryClientProvider>
    );
};

export const ReactQueryProvider = observer(ReactQueryProviderBase);
